/**
 * Intake print button with dropdown column settings.
 * Mirrors the AttendancePrint component pattern.
 */

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Printer, CheckSquare, Square } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { IntakePrintSettings } from "../hooks/useIntakePrint";

interface IntakePrintProps {
  printSettings: IntakePrintSettings;
  onPrintSettingsChange: (settings: IntakePrintSettings) => void;
  areAllColumnsSelected: boolean;
  onToggleAllColumns: (checked: boolean) => void;
  onPrint: () => void;
  disabled?: boolean;
}

type ColumnKey = keyof IntakePrintSettings;

interface ColumnDef {
  key: ColumnKey;
  translationKey: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "showAmount", translationKey: "intake:print.columns.showAmount" },
  { key: "showEnergy", translationKey: "intake:print.columns.showEnergy" },
  { key: "showProtein", translationKey: "intake:print.columns.showProtein" },
  { key: "showFat", translationKey: "intake:print.columns.showFat" },
  { key: "showCarbs", translationKey: "intake:print.columns.showCarbs" },
  { key: "showSatFat", translationKey: "intake:print.columns.showSatFat" },
  { key: "showFiber", translationKey: "intake:print.columns.showFiber" },
  { key: "showSodium", translationKey: "intake:print.columns.showSodium" },
  { key: "showNotes", translationKey: "intake:print.columns.showNotes" },
  {
    key: "showRecipeIngredients",
    translationKey: "intake:print.columns.showRecipeIngredients",
  },
  {
    key: "showCategoryTotals",
    translationKey: "intake:print.columns.showCategoryTotals",
  },
  {
    key: "showDailyTotals",
    translationKey: "intake:print.columns.showDailyTotals",
  },
];

export function IntakePrint({
  printSettings,
  onPrintSettingsChange,
  areAllColumnsSelected,
  onToggleAllColumns,
  onPrint,
  disabled = false,
}: IntakePrintProps) {
  const { t } = useTranslation();

  const updateSetting = (key: ColumnKey, value: boolean) => {
    onPrintSettingsChange({ ...printSettings, [key]: value });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Printer className="h-4 w-4 mr-2" />
          {t("intake:print.button")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onPrint} disabled={disabled}>
          {t("intake:print.printDailyIntake")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onToggleAllColumns(!areAllColumnsSelected)}
        >
          <div className="flex items-center gap-2">
            {areAllColumnsSelected ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {t("intake:print.selectAllColumns")}
          </div>
        </DropdownMenuItem>
        <div className="max-h-72 overflow-y-auto p-2">
          <div className="space-y-2">
            {COLUMNS.map(col => (
              <div
                key={col.key}
                className="flex items-center gap-2 p-1 hover:bg-accent rounded"
              >
                <Checkbox
                  id={`print-${col.key}`}
                  checked={printSettings[col.key]}
                  onCheckedChange={checked => updateSetting(col.key, !!checked)}
                />
                <Label
                  htmlFor={`print-${col.key}`}
                  className="cursor-pointer text-sm"
                >
                  {t(col.translationKey)}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
